import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle2, AlertCircle, Phone, Globe, MapPin, Mail, UserCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = { name: '', email: '', company: '', interest: '', message: '' };

const sectionMotion = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.6 },
};

const formMotion = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.6, delay: 0.15 },
};

const alertMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

const interestOptions = [
  { value: '', label: 'Select interest' },
  { value: 'investment', label: 'Investment Opportunity' },
  { value: 'partnership', label: 'Strategic Partnership' },
  { value: 'technology', label: 'Technology Licensing' },
  { value: 'supply', label: 'Supply Agreement' },
  { value: 'other', label: 'Other' },
];

const contacts = [
  { Icon: MapPin, primary: 'Lia Neumann \u2014 CEO', secondary: 'IBL Elements Inc., Oklahoma, USA', testId: 'contact-ceo' },
  { Icon: UserCircle, primary: 'Vladimir Dryaba \u2014 CCRO', secondary: 'Chief Client Relations Officer, IODX', testId: 'contact-ccro' },
  { Icon: Phone, href: 'tel:+14255020208', text: '+1 (425) 502-0208', testId: 'contact-phone' },
  { Icon: Mail, href: 'mailto:IODXreserve@gmail.com', text: 'IODXreserve@gmail.com', testId: 'contact-email' },
  { Icon: Globe, href: 'https://iblelements.com', text: 'iblelements.com', testId: 'contact-website', external: true },
];

const inputClass = "w-full rounded-sm border border-[rgba(59,130,246,0.15)] bg-[#060B1E] px-4 py-3 text-sm text-[#F1F5F9] placeholder-[#94A3B8]/40 transition-all duration-300 focus:border-[#3B82F6]/50";
const labelClass = "block text-xs font-semibold uppercase tracking-[0.15em] text-[#94A3B8] mb-2";
const iconBoxClass = "flex h-10 w-10 items-center justify-center rounded-sm bg-[rgba(59,130,246,0.08)] border border-[rgba(59,130,246,0.2)]";

function ContactInfoItem({ Icon, primary, secondary, href, text, testId, external }) {
  return (
    <div className="flex items-start gap-4">
      <div className={iconBoxClass}>
        <Icon className="w-4 h-4 text-[#3B82F6]" strokeWidth={1.5} />
      </div>
      <div>
        {href ? (
          <a
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="text-sm text-[#F1F5F9] hover:text-[#60A5FA] transition-colors"
            data-testid={testId}
          >
            {text}
          </a>
        ) : (
          <div data-testid={testId}>
            <div className="text-sm font-semibold text-[#F1F5F9]">{primary}</div>
            {secondary && <div className="text-sm text-[#94A3B8]">{secondary}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ContactInfo() {
  return (
    <motion.div {...sectionMotion}>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#3B82F6]">Get In Touch</div>
      <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#F1F5F9]">
        Request the full investor materials.
      </h2>
      <p className="mt-4 text-lg leading-relaxed text-[#94A3B8]">
        For partnership inquiries and investor access, reach out to our leadership team directly.
      </p>
      <div className="mt-10 space-y-6">
        {contacts.map((c) => (
          <ContactInfoItem key={c.testId} {...c} />
        ))}
      </div>
    </motion.div>
  );
}

function ContactForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle');

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(INITIAL_FORM);
    } catch {
      setStatus('error');
    }
  }, [form]);

  return (
    <motion.div {...formMotion}>
      <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Name *</label>
            <input type="text" name="name" required value={form.name} onChange={handleChange} data-testid="contact-input-name" className={inputClass} placeholder="Your full name" />
          </div>
          <div>
            <label className={labelClass}>Email *</label>
            <input type="email" name="email" required value={form.email} onChange={handleChange} data-testid="contact-input-email" className={inputClass} placeholder="investor@company.com" />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Company</label>
            <input type="text" name="company" value={form.company} onChange={handleChange} data-testid="contact-input-company" className={inputClass} placeholder="Organization name" />
          </div>
          <div>
            <label className={labelClass}>Area of Interest</label>
            <select name="interest" value={form.interest} onChange={handleChange} data-testid="contact-input-interest" className={inputClass}>
              {interestOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-[#0A1628]">{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Message *</label>
          <textarea name="message" required rows={4} value={form.message} onChange={handleChange} data-testid="contact-input-message" className={`${inputClass} resize-none`} placeholder="Tell us about your interest in the project..." />
        </div>

        <button
          type="submit" disabled={status === 'sending'} data-testid="contact-submit-button"
          className="flex items-center gap-2 rounded-sm bg-[#3B82F6] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:bg-[#60A5FA] hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === 'sending' ? 'Submitting...' : <><Send className="w-4 h-4" strokeWidth={1.5} />Submit Inquiry</>}
        </button>

        {status === 'success' && (
          <motion.div {...alertMotion} data-testid="contact-success-message" className="flex items-center gap-2 rounded-sm border border-green-500/20 bg-green-500/10 p-4">
            <CheckCircle2 className="w-5 h-5 text-green-400" strokeWidth={1.5} />
            <span className="text-sm text-green-300">Thank you! Your inquiry has been received. We'll be in touch shortly.</span>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div {...alertMotion} data-testid="contact-error-message" className="flex items-center gap-2 rounded-sm border border-red-500/20 bg-red-500/10 p-4">
            <AlertCircle className="w-5 h-5 text-red-400" strokeWidth={1.5} />
            <span className="text-sm text-red-300">Something went wrong. Please try again or contact us directly.</span>
          </motion.div>
        )}
      </form>
    </motion.div>
  );
}

export default function ContactSection() {
  return (
    <section id="contact" data-testid="contact-section" className="border-t border-[rgba(59,130,246,0.1)]" style={{ background: '#0A1628' }}>
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <div className="grid gap-16 lg:grid-cols-2 items-start">
          <ContactInfo />
          <ContactForm />
        </div>
      </div>
    </section>
  );
}
